import type { HpsfirProTableColumn } from 'hpsfir-managebase-ui';

type ColumnWidthRule = {
  width: number | undefined;
  test: (ctx: ColumnWidthCtx) => boolean;
};

type ColumnWidthCtx = {
  name: string;
  label: string;
  type: string;
  combined: string;
};

const normalizeText = (value: unknown) => String(value || '').toLowerCase();

const includesAny = (value: string, keys: string[]) => keys.some((key) => value.includes(key.toLowerCase()));

const longNameKeys = [
  'remark',
  'desc',
  'description',
  'reason',
  'url',
  'path',
  'secret',
  'meta',
  'link',
  'menus',
  'geo',
  'msg',
  'extjs',
  'versioninfo',
  'scene',
];

const longLabelKeys = [
  '备注',
  '描述',
  '原因',
  'url',
  '路径',
  'secret',
  '详细',
  '地址',
  '权限',
  '定位',
  '状态说明',
  'extjs',
  '线上版本',
  '场景',
];

const widthRules: ColumnWidthRule[] = [
  {
    width: 168,
    test: ({ combined }) => includesAny(combined, [
      'createTime',
      'updateTime',
      'expireTime',
      'packageEndTime',
      '授权时间',
      '创建时间',
      '更新时间',
      '过期时间',
      '到期时间',
      '加入时间',
      '开户时间',
    ]),
  },
  {
    width: 116,
    test: ({ combined }) => includesAny(combined, ['syncTime', '统计日期', '开门年月日']),
  },
  {
    width: 96,
    test: ({ name, label }) => name === 'time' || includesAny(label, ['开门时间']),
  },
  {
    width: 128,
    test: ({ combined }) => includesAny(combined, ['phone', '手机号', '手机号码', '电话']),
  },
  {
    width: 180,
    test: ({ name }) => includesAny(name, ['appid', 'weappappid', 'targetappid']),
  },
  {
    width: 190,
    test: ({ name }) => includesAny(name, ['idhex', 'serialnumber']),
  },
  {
    width: 112,
    test: ({ name, label }) => includesAny(name, ['templateid', 'draftid']) || includesAny(label, ['模板id', '草稿id']),
  },
  {
    width: 150,
    test: ({ name, label }) => name === 'id' || label === '点券码',
  },
  {
    width: 104,
    test: ({ name, label }) => includesAny(name, ['version']) || includesAny(label, ['版本号', '当前版本', '用户版本', '版本']),
  },
  {
    width: 80,
    test: ({ label }) => includesAny(label, ['序号']),
  },
  {
    width: 112,
    test: ({ name, type }) => type === 'number'
      || type === 'numberstr'
      || includesAny(name, ['count', 'amount', 'limit', 'days', 'distance', 'bean', 'sequence']),
  },
  {
    width: 128,
    test: ({ type }) => type === 'swtich',
  },
  {
    width: undefined,
    test: ({ name }) => includesAny(name, ['weapptplids', 'wxtplids', 'menus']),
  },
  {
    width: 136,
    test: ({ combined, type }) => ['select', 'searchselect', 'multiselect'].includes(type)
      && includesAny(combined, ['status', '状态', '类型', '模板', '权限']),
  },
  {
    width: 150,
    test: ({ type }) => ['select', 'searchselect', 'multiselect'].includes(type),
  },
  {
    width: 120,
    test: ({ type }) => type === 'image',
  },
  {
    width: 136,
    test: ({ type }) => type === 'password',
  },
  {
    width: 112,
    test: ({ combined }) => includesAny(combined, ['province', 'city', '省份', '服务市']),
  },
  {
    width: 168,
    test: ({ combined }) => includesAny(combined, ['name', '名称', '姓名', '公司', '主体']),
  },
];

const isLongColumn = ({ name, label }: ColumnWidthCtx) => includesAny(name, longNameKeys) || includesAny(label, longLabelKeys);

const getBizColumnWidth = (column: HpsfirProTableColumn): number | undefined => {
  const ctx = {
    name: normalizeText(column.name),
    label: normalizeText(column.label),
    type: normalizeText(column.type),
    combined: `${normalizeText(column.name)} ${normalizeText(column.label)}`,
  };

  if (isLongColumn(ctx)) {
    return undefined;
  }

  return widthRules.find((rule) => rule.test(ctx))?.width ?? 140;
};

export const normalizeBizTableColumns = <T extends HpsfirProTableColumn>(columns: T[]): T[] => columns.map((column) => {
  if (column.hideTable) {
    return column;
  }

  const next = { ...column } as T & { width?: number; showOverflowTooltip?: boolean };
  const width = getBizColumnWidth(column);

  if (width === undefined) {
    delete next.width;
  } else {
    next.width = width;
  }

  // 本项目业务表格统一换行展示，不使用省略/悬浮提示承载主要内容。
  delete next.showOverflowTooltip;

  return next;
});
